import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

// =============================================================================
// PALETAS DE COLOR — sincronizadas con src/styles/_colors.scss
// Cada color base ocupa el tono 500; el resto son variaciones de luminosidad.
// Referencia en tokens PrimeNG: {primary.500}, {success.300}, etc.
// =============================================================================

// --color-primary / --color-brand: #054c31
const primaryPalette = {
  50:  '#e6f2ec',
  100: '#b3d9c5',
  200: '#80c09e',
  300: '#4da777',
  400: '#268e55',
  500: '#054c31',
  600: '#044428',
  700: '#033b22',
  800: '#02321b',
  900: '#012815',
  950: '#011a0d',
};

// --color-secondary: #64748b
const secondaryPalette = {
  50:  '#f8fafc',
  100: '#d1fae5',//'#f1f5f9',
  200: '#e2e8f0',
  300: '#cbd5e1',
  400: '#94a3b8',
  500: '#64748b',
  600: '#475569',
  700: '#334155',
  800: '#1e293b',
  900: '#0f172a',
  950: '#020617',
};

// --color-success: #16a34a
const successPalette = {
  50:  '#e8f9f0',
  100: '#c0f0d5',
  200: '#98e7ba',
  300: '#5fd898',
  400: '#2cc972',
  500: '#16a34a',
  600: '#128f40',
  700: '#0f7a36',
  800: '#0b642c',
  900: '#084d21',
  950: '#053315',
};

// --color-info: #0369a1
const infoPalette = {
  50:  '#e6f3fa',
  100: '#b3d9f0',
  200: '#80bfe6',
  300: '#4da5dc',
  400: '#2692d3',
  500: '#0369a1',
  600: '#025d8e',
  700: '#02507a',
  800: '#014366',
  900: '#013551',
  950: '#012337',
};

// --color-warn: #d97706
const warnPalette = {
  50:  '#fef9e6',
  100: '#fcedb3',
  200: '#fae180',
  300: '#f8d54d',
  400: '#f6c926',
  500: '#d97706',
  600: '#be6905',
  700: '#a35b04',
  800: '#884d03',
  900: '#6d3d02',
  950: '#4a2901',
};

// --color-danger: #dc2626
const dangerPalette = {
  50:  '#fef2f2',
  100: '#fee2e2',
  200: '#fecaca',
  300: '#fc9a9a',
  400: '#f76666',
  500: '#dc2626',
  600: '#c01f1f',
  700: '#a31919',
  800: '#861313',
  900: '#6a0f0f',
  950: '#480a0a',
};

// --color-help: #7c3aed
const helpPalette = {
  50:  '#f5f0fe',
  100: '#e0d0fb',
  200: '#ccb0f9',
  300: '#b086f6',
  400: '#9460f2',
  500: '#7c3aed',
  600: '#6d32d3',
  700: '#5e2ab8',
  800: '#4e229d',
  900: '#3e1a81',
  950: '#2a1158',
};

// --color-teal: #0f766e
const tealPalette = {
  50:  '#e6f5f4',
  100: '#b3e2de',
  200: '#80cfc8',
  300: '#4dbcb2',
  400: '#26ad9f',
  500: '#0f766e',
  600: '#0d6660',
  700: '#0b5651',
  800: '#094542',
  900: '#073532',
  950: '#042220',
};

// --color-pink: #be185d
const pinkPalette = {
  50:  '#fde9f2',
  100: '#fac5e0',
  200: '#f79fcc',
  300: '#f275b4',
  400: '#e94898',
  500: '#be185d',
  600: '#a71452',
  700: '#901046',
  800: '#780d3a',
  900: '#600a2e',
  950: '#40071e',
};

export const AppPreset = definePreset(Aura, {
  // Fuente global para todos los componentes PrimeNG
  primitive: {
    fontFamily: "'Montserrat', sans-serif",
  },
  semantic: {
    primary:   primaryPalette,
    secondary: secondaryPalette,
    success:   successPalette,
    info:      infoPalette,
    warn:      warnPalette,
    danger:    dangerPalette,
    help:      helpPalette,
    teal:      tealPalette,
    pink:      pinkPalette,
    colorScheme: {
      light: {
        primary: {
          color:           '{primary.500}',
          contrastColor:   '#ffffff',
          hoverColor:      '{primary.400}',
          activeColor:     '{primary.600}',
        },
        highlight: {
          background:      '{primary.500}',
          focusBackground: '{primary.400}',
          color:           '#ffffff',
          focusColor:      '#ffffff',
        },
      },
    },
  },

  // =============================================================================
  // OVERRIDES DE COMPONENTES — aplican las paletas a los componentes de severidad
  // Los tokens van al nivel raíz del componente (no bajo colorScheme.light).
  // Nota: Message / InlineMessage / Toast usan 'error' en lugar de 'danger'.
  // =============================================================================
  components: {

    // -------------------------------------------------------------------------
    // Button — colorScheme > light > root > {severity}
    // -------------------------------------------------------------------------
    button: {
      colorScheme: {
        light: {
          root: {
            secondary: {
              background:        '{secondary.500}',
              hoverBackground:   '{secondary.400}',
              activeBackground:  '{secondary.600}',
              borderColor:       '{secondary.500}',
              hoverBorderColor:  '{secondary.400}',
              activeBorderColor: '{secondary.600}',
              color:             '#ffffff',
              hoverColor:        '#ffffff',
              activeColor:       '#ffffff',
            },
            success: {
              background:        '{success.500}',
              hoverBackground:   '{success.400}',
              activeBackground:  '{success.600}',
              borderColor:       '{success.500}',
              hoverBorderColor:  '{success.400}',
              activeBorderColor: '{success.600}',
              color:             '#ffffff',
              hoverColor:        '#ffffff',
              activeColor:       '#ffffff',
            },
            info: {
              background:        '{info.500}',
              hoverBackground:   '{info.400}',
              activeBackground:  '{info.600}',
              borderColor:       '{info.500}',
              hoverBorderColor:  '{info.400}',
              activeBorderColor: '{info.600}',
              color:             '#ffffff',
              hoverColor:        '#ffffff',
              activeColor:       '#ffffff',
            },
            warn: {
              background:        '{warn.500}',
              hoverBackground:   '{warn.400}',
              activeBackground:  '{warn.600}',
              borderColor:       '{warn.500}',
              hoverBorderColor:  '{warn.400}',
              activeBorderColor: '{warn.600}',
              color:             '#ffffff',
              hoverColor:        '#ffffff',
              activeColor:       '#ffffff',
            },
            danger: {
              background:        '{danger.500}',
              hoverBackground:   '{danger.400}',
              activeBackground:  '{danger.600}',
              borderColor:       '{danger.500}',
              hoverBorderColor:  '{danger.400}',
              activeBorderColor: '{danger.600}',
              color:             '#ffffff',
              hoverColor:        '#ffffff',
              activeColor:       '#ffffff',
            },
            help: {
              background:        '{help.500}',
              hoverBackground:   '{help.400}',
              activeBackground:  '{help.600}',
              borderColor:       '{help.500}',
              hoverBorderColor:  '{help.400}',
              activeBorderColor: '{help.600}',
              color:             '#ffffff',
              hoverColor:        '#ffffff',
              activeColor:       '#ffffff',
            },
          },
        },
      },
    },

    // -------------------------------------------------------------------------
    // Tag — colorScheme > light > root > {severity}
    // -------------------------------------------------------------------------
    tag: {
      colorScheme: {
        light: {
          secondary: { background: '{secondary.100}', color: '{secondary.700}' },
          success:   { background: '{success.100}',   color: '{success.700}'   },
          info:      { background: '{info.100}',      color: '{info.700}'      },
          warn:      { background: '{warn.100}',      color: '{warn.700}'      },
          danger:    { background: '{danger.100}',    color: '{danger.700}'    },
        },
      },
    },

    // // -------------------------------------------------------------------------
    // // Badge — colorScheme > light > root > {severity}
    // // -------------------------------------------------------------------------
    badge: {
      colorScheme: {
        light: {
          secondary: { background: '{secondary.500}', color: '#ffffff' },
          success:   { background: '{success.500}',   color: '#ffffff' },
          info:      { background: '{info.500}',      color: '#ffffff' },
          warn:      { background: '{warn.500}',      color: '#ffffff' },
          danger:    { background: '{danger.500}',    color: '#ffffff' },
        },
      },
    },

    // // -------------------------------------------------------------------------
    // // Message — colorScheme > light > root > {severity}  (usa 'error' por 'danger')
    // // -------------------------------------------------------------------------
    message: {
      colorScheme: {
        light: {
          success:   { background: '{success.50}',   borderColor: '{success.500}',   color: '{success.700}'   },
          info:      { background: '{info.50}',      borderColor: '{info.500}',      color: '{info.700}'      },
          warn:      { background: '{warn.50}',      borderColor: '{warn.500}',      color: '{warn.700}'      },
          error:     { background: '{danger.50}',    borderColor: '{danger.500}',    color: '{danger.700}'    },
          secondary: { background: '{secondary.50}', borderColor: '{secondary.300}', color: '{secondary.700}' },
        },
      },
    },

    // // -------------------------------------------------------------------------
    // // InlineMessage — colorScheme > light > root > {severity}
    // // -------------------------------------------------------------------------
    // inlinemessage: {
    //   colorScheme: {
    //     light: {
    //       root: {
    //         success:   { background: '{success.50}',   borderColor: '{success.500}',   color: '{success.700}'   },
    //         info:      { background: '{info.50}',      borderColor: '{info.500}',      color: '{info.700}'      },
    //         warn:      { background: '{warn.50}',      borderColor: '{warn.500}',      color: '{warn.700}'      },
    //         error:     { background: '{danger.50}',    borderColor: '{danger.500}',    color: '{danger.700}'    },
    //         secondary: { background: '{secondary.50}', borderColor: '{secondary.300}', color: '{secondary.700}' },
    //       },
    //     },
    //   },
    // },

    // // -------------------------------------------------------------------------
    // // Toast — colorScheme > light > root > {severity}
    // // -------------------------------------------------------------------------
    toast: {
      colorScheme: {
        light: {
          success:   { background: '{success.50}',   borderColor: '{success.500}',   color: '{success.700}',   detailColor: '{success.600}'   },
          info:      { background: '{info.50}',      borderColor: '{info.500}',      color: '{info.700}',      detailColor: '{info.600}'      },
          warn:      { background: '{warn.50}',      borderColor: '{warn.500}',      color: '{warn.700}',      detailColor: '{warn.600}'      },
          error:     { background: '{danger.50}',    borderColor: '{danger.500}',    color: '{danger.700}',    detailColor: '{danger.600}'    },
          secondary: { background: '{secondary.50}', borderColor: '{secondary.300}', color: '{secondary.700}', detailColor: '{secondary.600}' },
        },
      },
    },

  },
});
